import { Link, useLocation, useNavigate, useSearchParams, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  IoHomeOutline,
  IoHomeSharp,
  IoLayersOutline,
  IoLayersSharp,
  IoCubeOutline,
  IoCubeSharp,
  IoPricetagsOutline,
  IoPricetagsSharp,
  IoListOutline,
  IoListSharp,
} from 'react-icons/io5'

/**
 * Bottom navigation optimized for thumb reach on iPad Mini
 * - Fixed at bottom
 * - Large touch targets (44x44pt minimum)
 * - Safe area insets support
 */
export default function BottomNavigation() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const { brandId: brandIdFromParams } = useParams<{ brandId?: string }>()
  const brandId = searchParams.get('brandId') || brandIdFromParams || undefined
  const brandQs = brandId ? `?brandId=${encodeURIComponent(brandId)}` : ''

  const handleListingsClick = (e: React.MouseEvent) => {
    e.preventDefault()
    // Invalidate and refetch listings to ensure fresh data
    queryClient.invalidateQueries({ queryKey: ['listings'] })
    queryClient.invalidateQueries({ queryKey: ['listings', 'active'] })
    navigate(`/listings${brandQs}`)
  }

  const navItems = [
    {
      path: '/',
      label: 'Dashboard',
      icon: IoHomeOutline,
      activeIcon: IoHomeSharp,
    },
    {
      path: '/stock-items',
      label: 'Stock',
      icon: IoLayersOutline,
      activeIcon: IoLayersSharp,
    },
    {
      path: '/bundles',
      label: 'Bundles',
      icon: IoCubeOutline,
      activeIcon: IoCubeSharp,
    },
    {
      path: '/products',
      label: 'Products',
      icon: IoPricetagsOutline,
      activeIcon: IoPricetagsSharp,
    },
    {
      path: '/listings',
      label: 'Listings',
      icon: IoListOutline,
      activeIcon: IoListSharp,
    },
    {
      path: '/inventory',
      label: 'Inventory',
      icon: IoLayersOutline,
      activeIcon: IoLayersSharp,
    },
  ] as const

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-bottom">
      <div className="flex justify-around items-center px-2 py-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path
          const Icon = (isActive ? item.activeIcon : item.icon) as React.ComponentType<{ className?: string }>
          
          // Special handling for listings to bust cache
          if (item.path === '/listings') {
            return (
              <button
                key={item.path}
                onClick={handleListingsClick}
                className={`
                  flex flex-col items-center justify-center
                  touch-target-min-44 px-4 py-2 rounded-lg
                  transition-colors duration-200
                  ${isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}
                `}
              >
                <span className="text-2xl mb-1">
                  <Icon className="w-6 h-6" />
                </span>
                <span className="text-xs font-medium">{item.label}</span>
              </button>
            )
          }
          
          return (
            <Link
              key={item.path}
              to={`${item.path}${brandQs}`}
              className={`
                flex flex-col items-center justify-center
                touch-target-min-44 px-4 py-2 rounded-lg
                transition-colors duration-200
                ${isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}
              `}
            >
              <span className="text-2xl mb-1">
                <Icon className="w-6 h-6" />
              </span>
              <span className="text-xs font-medium">{item.label}</span>
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
